import React, {
  useImperativeHandle,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react'
import {
  ChargingSpecsAdd,
  ChargePointsListItem,
  ChargePointsListItemSkeleton,
} from 'Components'
import Styles from './ChargePointsList.module.css'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { EmptySpecs } from 'Components/IFEmptyStates/IFEmptyStates'
import { Virtuoso } from 'react-virtuoso'
import { useOverlayScrollbars } from 'overlayscrollbars-react'
import 'overlayscrollbars/overlayscrollbars.css'
import OperatorAlertsActions from 'Stores/Alert/Actions'
import OperatorAlertsSelectors from 'Stores/Alert/Selectors'
import IFFilterType from 'Enums/IFFilterType'
import { connect } from 'react-redux'
import RequestState from 'Enums/RequestState'
import AuthSelectors from 'Stores/Auth/Selectors'
import InfinityEnums from 'Enums/InfinityEnums'

const ChargePointsList = React.forwardRef(
  (
    {
      displayedList,
      dropdownList,
      isEditing,
      isLoading,
      maxListHeight,
      scrollbarsHeight,
      onSubmit,
      alertChargePointsFilter,
      setAlertChargePointsFilter,
      fetchAlertChargePoints,
      paginationOffset,
      resetAlertChargePoints,
      fetchAlertChargePointsRequestState,
      operatorUser,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const [deleteChargePoint, setDeleteChargePoint] = useState([])
    const [addChargePoints, setAddChargePoints] = useState([])
    const [newChargePoint, setNewChargePoint] = useState([])
    const [dynamicHeight, setDynamicHeight] = useState(0)
    const [combinedChargePoints, setCombinedChargePoints] =
      useState(displayedList)
    const rootRef = useRef(null)
    const options = {
      scrollbars: {
        visibility: 'auto',
        autoHide: 'move',
        autoHideDelay: 1000,
      },
    }
    const [scroller, setScroller] = useState(null)
    const [initialize, osInstance] = useOverlayScrollbars({
      options,
      defer: true,
    })
    const canEditAlert = operatorUser.permissions.includes(
      InfinityEnums.OperatorUserPermission.CAN_EDIT_ALERT,
    )

    useEffect(() => {
      const { current: root } = rootRef
      if (scroller && root) {
        initialize({
          target: root,
          elements: {
            viewport: scroller,
          },
        })
      }
      return () => osInstance()?.destroy()
    }, [scroller, initialize, osInstance])

    useEffect(() => {
      const errorHandler = (e) => {
        if (
          e.message.includes(
            'ResizeObserver loop completed with undelivered notifications' ||
              'ResizeObserver loop limit exceeded',
          )
        ) {
          const resizeObserverErr = document.getElementById(
            'webpack-dev-server-client-overlay',
          )
          if (resizeObserverErr) {
            resizeObserverErr.style.display = 'none'
          }
        }
      }
      window.addEventListener('error', errorHandler)
      return () => {
        window.removeEventListener('error', errorHandler)
      }
    }, [])

    // Work around for displayedList firing useEffect without it's value changing
    const [newDisplayedList, setNewDisplayedList] = useState(displayedList)
    useEffect(() => {
      if (JSON.stringify(displayedList) !== JSON.stringify(newDisplayedList)) {
        setNewDisplayedList(displayedList)
      }
    }, [displayedList])

    useEffect(() => {
      setCombinedChargePoints((prev = []) => {
        const newOnes = prev.filter((g) => g.origin === 'new')
        const originals = (displayedList || []).map((g) => ({
          ...g,
          origin: 'original',
        }))
        return [...originals]
      })
    }, [displayedList])

    useEffect(() => {
      setDeleteChargePoint([])
      setAddChargePoints([])
      setNewChargePoint([])
    }, [isEditing])

    const resetValues = () => {
      setDeleteChargePoint([])
      setNewChargePoint([])
      setAddChargePoints([])
      setCombinedChargePoints((prevState) => {
        return prevState?.filter((group) => group.origin === 'original')
      })
    }

    const handleCancel = () => {
      resetValues()
    }

    const keyOf = (cp) => cp?._id ?? cp?.huid ?? cp?.chargePointHuid ?? cp?.id
    const handleSubmit = (resetValuesFlag) => {
      const deletedKeys = new Set(deleteChargePoint.map(keyOf))
      const originalsKept = (displayedList || []).filter(
        (cp) => !deletedKeys.has(keyOf(cp)),
      )
      const added = newChargePoint.filter((cp) =>
        addChargePoints.includes(keyOf(cp)),
      )
      const finalList = [...originalsKept, ...added]

      const payload = {
        addIds: [...addChargePoints],
        deleteIds: Array.from(deletedKeys),
        finalList,
      }

      onSubmit?.(payload)

      if (resetValuesFlag) resetValues()
      return payload
    }

    const didChange = () => {
      return deleteChargePoint.length > 0 || addChargePoints.length > 0
    }

    const handleAdd = (_id) => {
      if (!addChargePoints.includes(_id)) {
        setAddChargePoints((prev) => [...prev, _id])
      }

      const src = dropdownList.find((g) => g._id === _id)
      if (src && !newChargePoint.some((g) => g._id === _id)) {
        const normalized = {
          _id: src._id ?? src.id,
          stationName: src.stationName ?? '',
          chargePointHuid: src.chargePointHuid ?? '',
          name: src.name ?? '',
          origin: 'new',
        }
        setCombinedChargePoints((prev) => [...prev, normalized])
        setNewChargePoint((prev) => [...prev, normalized])
      }
    }
    const handleChargePointDelete = (chargePoints) => {
      const toRemove = Array.isArray(chargePoints)
        ? chargePoints
        : [chargePoints]

      setDeleteChargePoint((prev) => {
        const remaining = prev.filter(
          (group) =>
            !toRemove.some(
              (cp) => cp.chargePointHuid === group.chargePointHuid,
            ),
        )
        const newOnes = toRemove.filter(
          (cp) =>
            !prev.some((group) => group.chargePointHuid === cp.chargePointHuid),
        )
        return [...remaining, ...newOnes]
      })
    }

    const handleChargePointAdd = (chargePointId) => {
      const index = addChargePoints.findIndex(
        (groupId) => groupId === chargePointId,
      )
      if (index === -1) {
        setAddChargePoints((current) => [...current, chargePointId])
      } else {
        setAddChargePoints(addChargePoints.filter((id) => id !== chargePointId))
      }
    }

    useImperativeHandle(ref, () => ({
      handleCancel,
      handleSubmit,
      didChange,
    }))

    useEffect(() => {
      if (canEditAlert)
        fetchAlertChargePoints(alertChargePointsFilter, paginationOffset)
    }, [alertChargePointsFilter])

    const handleSearch = (searchQuery) => {
      resetAlertChargePoints()
      const updatedFilters = alertChargePointsFilter.map((flt) =>
        flt.type === IFFilterType.KEYWORD
          ? {
              ...flt,
              value: searchQuery ? [searchQuery] : [],
            }
          : flt,
      )
      setAlertChargePointsFilter(updatedFilters)
    }

    const loadMoreChargePoints = () => {
      if (
        fetchAlertChargePointsRequestState === RequestState.LOADING_MORE ||
        paginationOffset === null
      )
        return
      fetchAlertChargePoints(alertChargePointsFilter, paginationOffset)
    }
    const presentHuids = useMemo(() => {
      return new Set(
        (combinedChargePoints || [])
          .map((cp) => cp.chargePointHuid ?? cp.huid)
          .filter(Boolean),
      )
    }, [combinedChargePoints])
    const availableDropdown = useMemo(() => {
      return (dropdownList || []).filter(
        (cp) => !presentHuids.has(cp.chargePointHuid ?? cp.huid),
      )
    }, [dropdownList, presentHuids])
    return (
      <div
        className={
          isEditing
            ? Styles.ChargePointListWrapperEditing
            : Styles.ChargePointListWrapper
        }
        style={{
          height: combinedChargePoints.length > 0 ? dynamicHeight : '',
          maxHeight: maxListHeight,
        }}
      >
        {displayedList?.length === 0 &&
        newChargePoint.length === 0 &&
        !isLoading ? (
          <div
            className={Styles.EmptyContainer}
            style={isEditing ? { height: '4.25rem', marginTop: '0.5rem' } : {}}
          >
            <EmptySpecs title={t('ChargePointList.NoChargePointsFound')} />
          </div>
        ) : isLoading ? (
          <div style={{ height: scrollbarsHeight, paddingRight: '1rem' }}>
            {[...Array(6)].map((index) => (
              <ChargePointsListItemSkeleton key={index} />
            ))}
          </div>
        ) : (
          <div
            className={
              isEditing ? Styles.ListWrapperEditing : Styles.ListWrapper
            }
            data-overlayscrollbars=""
          >
            <Virtuoso
              scrollerRef={(ref) => {
                if (ref) {
                  setScroller(ref)
                }
              }}
              style={{ height: '100%' }}
              data={combinedChargePoints}
              increaseViewportBy={480}
              totalListHeightChanged={(height) => {
                setDynamicHeight(height + combinedChargePoints.length * 8 + 40)
              }}
              itemContent={(index, chargePoint) => {
                if (chargePoint && chargePoint.origin === 'original') {
                  return (
                    <div
                      className={Styles.ListItem}
                      style={{
                        marginBottom:
                          index === newChargePoint.length - 1
                            ? '0rem'
                            : '0.5rem',
                      }}
                    >
                      <ChargePointsListItem
                        stationName={chargePoint.stationName}
                        chargePointHuid={
                          chargePoint.chargePointHuid ?? chargePoint.huid ?? ''
                        }
                        isEditing={isEditing}
                        onIconClick={() => {
                          handleChargePointDelete(chargePoint)
                        }}
                      />
                    </div>
                  )
                } else {
                  return (
                    <div
                      className={Styles.ListItem}
                      style={{
                        marginBottom:
                          index === newChargePoint.length - 1
                            ? '0rem'
                            : '0.5rem',
                      }}
                    >
                      <ChargePointsListItem
                        stationName={chargePoint.stationName}
                        chargePointHuid={
                          chargePoint.chargePointHuid ?? chargePoint.huid ?? ''
                        }
                        isEditing={isEditing}
                        onIconClick={() => {
                          handleChargePointAdd(chargePoint._id)
                        }}
                      />
                    </div>
                  )
                }
              }}
            />
          </div>
        )}
        {isEditing && !isLoading && (
          <div className={Styles.AddContainer}>
            <ChargingSpecsAdd
              key={1}
              data={availableDropdown}
              onSelect={handleAdd}
              RenderMenuItem={ChargePointsListItem}
              RenderMenuItemkeys={['stationName', 'chargePointHuid']}
              infiniteLoader={true}
              onSearch={(searchQuery) => {
                handleSearch(searchQuery)
              }}
              loading={
                fetchAlertChargePointsRequestState === RequestState.LOADING
              }
              paginationOffset={paginationOffset}
              loadMore={loadMoreChargePoints}
            />
          </div>
        )}
      </div>
    )
  },
)

function mapDispatchToProps(dispatch) {
  return {
    fetchAlertChargePoints: (filter, offset) => {
      dispatch(OperatorAlertsActions.fetchAlertChargePoints(filter, offset))
    },
    setAlertChargePointsFilter: (filter) => {
      dispatch(OperatorAlertsActions.setAlertChargePointsFilter(filter))
    },
    resetAlertChargePoints: () => {
      dispatch(OperatorAlertsActions.resetAlertChargePoints())
    },
  }
}

const mapStateToProps = (state) => ({
  alertChargePointsFilter:
    OperatorAlertsSelectors.getAlertChargePointsFilter(state),
  paginationOffset:
    OperatorAlertsSelectors.getAlertChargePointsPaginationOffset(state),

  fetchAlertChargePointsRequestState:
    OperatorAlertsSelectors.getFetchAlertChargePointsRequestState(state),
  operatorUser: AuthSelectors.getOperatorUser(state),
})
ChargePointsList.propTypes = {
  displayedList: PropTypes.arrayOf(PropTypes.object),
  dropdownList: PropTypes.arrayOf(PropTypes.object),
  isEditing: PropTypes.bool,
  isLoading: PropTypes.bool,
  maxListHeigh: PropTypes.number,
  scrollbarsHeight: PropTypes.string,
  onSubmit: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(ChargePointsList)
